import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, Spin, Tag, message } from 'antd';
import { ExportOutlined, SaveOutlined } from '@ant-design/icons';
import { get, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import type { AISettingType } from './types';
import './AIManagement.css';

const { TextArea } = Input;
const FIXED_BASE_URL = 'https://api.quickrouter.ai/v1';
const QUICKROUTER_SITE_URL = 'https://quickrouter.ai/';

const MODEL_OPTIONS = [
  { label: 'Claude Opus 5（最新旗舰）', value: 'claude-opus-5' },
  { label: 'Claude Opus 4.6（高质量）', value: 'claude-opus-4-6' },
  { label: 'DeepSeek V4 Pro（高质量）', value: 'deepseek-v4-pro' },
  { label: 'DeepSeek V4 Flash（轻量快速）', value: 'deepseek-v4-flash' },
];

const IMAGE_MODEL_OPTIONS = [
  { label: '织梦文生图（公司内部免费，首选）', value: 'polaris-text-image' },
  { label: '可灵 Kling v1（首个付费兜底）', value: 'kling-v1' },
  { label: '可灵 Kling v1.5（便宜，画面更稳）', value: 'kling-v1-5' },
  { label: '可灵 Kling v2.1（质量兜底）', value: 'kling-v2-1' },
  { label: 'Gemini 2.5 Flash Image（快速兜底）', value: 'gemini-2.5-flash-image' },
  { label: 'Gemini 3 Pro Image Preview（高质量兜底）', value: 'gemini-3-pro-image-preview' },
  { label: 'GPT Image 1（昂贵，最后兜底）', value: 'gpt-image-1' },
  { label: 'GPT Image 2（昂贵，高质量）', value: 'gpt-image-2' },
  { label: 'DALL·E 3（昂贵，兼容兜底）', value: 'dall-e-3' },
];

const IMAGE_SIZE_OPTIONS = [
  { label: '1024 × 1536（竖版封面）', value: '1024x1536' },
  { label: '1024 × 1024（方图测试）', value: '1024x1024' },
  { label: '768 × 1024（轻量竖图）', value: '768x1024' },
];

const AIConfigPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<AISettingType | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<AISettingType>(API_ENDPOINTS.ai_management.config);
      setConfig(data);
      form.setFieldsValue({
        base_url: data.base_url,
        model: data.model,
        image_model: data.image_model,
        image_fallback_model: data.image_fallback_model,
        image_size: data.image_size,
        api_key: '',
        system_prompt: data.system_prompt,
        temperature: data.temperature,
        max_tokens: data.max_tokens,
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载AI配置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await put(API_ENDPOINTS.ai_management.config, {
        model: values.model,
        image_model: values.image_model,
        image_fallback_model: values.image_fallback_model,
        image_size: values.image_size,
        api_key: values.api_key,
        system_prompt: values.system_prompt,
        temperature: values.temperature,
        max_tokens: values.max_tokens,
      });
      message.success('AI配置已保存');
      await loadConfig();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ai-management-page">
      <h1 className="page-title">AI配置</h1>

      <Card bordered={false} className="ai-section-card">
        <Spin spinning={loading}>
          {config && (
            <Space wrap className="ai-summary">
              <Tag color="blue">
                提供商: {config.provider}
                <a className="ai-provider-link" href={QUICKROUTER_SITE_URL} target="_blank" rel="noreferrer">
                  官网/充值 <ExportOutlined />
                </a>
              </Tag>
              <Tag color="geekblue">写作模型: {config.model}</Tag>
              <Tag color="cyan">生图首选: {config.image_model}</Tag>
              <Tag color="gold">生图兜底: {config.image_fallback_model}</Tag>
              <Tag color="lime">真实成本计费: 1 元 = 10 墨水</Tag>
              <Tag color={config.has_api_key ? 'green' : 'red'}>
                Key: {config.has_api_key ? '已配置' : '未配置'}
              </Tag>
              <Tag color="purple">Base URL: {config.base_url}</Tag>
            </Space>
          )}

          <Alert
            type="info"
            showIcon
            message="保存后，测试页和Web 前台AI创作都会真实使用这里的模型配置。写作模型用于小说文本，生图模型用于封面生成；首选模型失败时会自动尝试兜底模型。Key 不回显明文，留空时会保留当前值。"
            description="织梦文生图为公司内部免费能力，默认优先使用且不计外部模型成本；失败、超时或没有返回有效图片时，才切换到 QuickRouter 兜底模型。"
            style={{ marginBottom: 16 }}
          />

          <Form form={form} layout="vertical">
            <Form.Item
              label="Base URL"
              name="base_url"
            >
              <Input placeholder={FIXED_BASE_URL} disabled />
            </Form.Item>

            <Form.Item
              label="模型"
              name="model"
              rules={[{ required: true, message: '请输入模型名称' }]}
              extra="支持 Claude Opus 5、Claude Opus 4.6、DeepSeek V4 Pro 和 DeepSeek V4 Flash；DeepSeek 会自动使用低推理强度，优先尽快返回正文。"
            >
              <Select options={MODEL_OPTIONS} placeholder="请选择模型" />
            </Form.Item>

            <div className="ai-form-grid">
              <Form.Item
                label="首选生图模型"
                name="image_model"
                rules={[{ required: true, message: '请选择生图模型' }]}
                extra="用于用户端小说封面生成。默认选择公司内部免费的织梦文生图；失败、超时或没有返回有效图片时，后端才继续尝试兜底模型池。"
              >
                <Select options={IMAGE_MODEL_OPTIONS} placeholder="请选择生图模型" />
              </Form.Item>

              <Form.Item
                label="兜底生图模型"
                name="image_fallback_model"
                rules={[{ required: true, message: '请选择兜底生图模型' }]}
                extra="织梦首选失败、超时或图片下载解密失败时，会自动切到这里；默认使用可灵 Kling v1。"
              >
                <Select options={IMAGE_MODEL_OPTIONS} placeholder="请选择兜底生图模型" />
              </Form.Item>
            </div>

            <div className="ai-form-grid">
              <Form.Item
                label="默认生图尺寸"
                name="image_size"
                rules={[{ required: true, message: '请选择默认生图尺寸' }]}
              >
                <Select options={IMAGE_SIZE_OPTIONS} placeholder="请选择默认尺寸" />
              </Form.Item>
            </div>

            <Form.Item
              label={(
                <Space size={8}>
                  <span>API Key</span>
                  <Tag color={config?.has_api_key ? 'green' : 'red'}>
                    {config?.has_api_key ? '已保存' : '未保存'}
                  </Tag>
                </Space>
              )}
              name="api_key"
              extra={config?.has_api_key ? '当前 Key 已保存，输入新 Key 后会覆盖旧值。' : '请输入 QuickRouter API Key。'}
            >
              <Input.Password placeholder={config?.has_api_key ? '已保存，留空不修改' : '请输入 API Key'} />
            </Form.Item>

            <Form.Item label="系统提示词" name="system_prompt">
              <TextArea rows={5} placeholder="请输入系统提示词" />
            </Form.Item>

            <div className="ai-form-grid">
              <Form.Item
                label="温度"
                name="temperature"
                rules={[{ required: true, message: '请输入温度' }]}
              >
                <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="最大输出"
                name="max_tokens"
                rules={[{ required: true, message: '请输入最大输出' }]}
              >
                <InputNumber min={1} max={4096} step={1} style={{ width: '100%' }} />
              </Form.Item>

            </div>
          </Form>
        </Spin>
      </Card>

      <div className="ai-actions">
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存配置
        </Button>
      </div>
    </div>
  );
};

export default AIConfigPage;
